
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Supplier Details
//------------------------------------------------
const SupplierDetailsContent = ({ supplier, t }) => {
  if (!supplier) return null;

  const contactName = [
    supplier.firstName,
    supplier.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  const details = [
    [t("companyName"), supplier.company],
    [t("contactPerson"), contactName],
    [t("phone"), supplier.phone],
    [t("address"), supplier.address],
    [t("productsSale"), supplier.productSale],
    [t("note"), supplier.note],
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("supplierDetails")}
      </Typography>

      <TableContainer>
        <MuiTable size="small">
          <TableBody>
            {details.map(([label, value], index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    width: "40%",
                    fontWeight: "bold",
                  }}
                >
                  {label}
                </TableCell>

                <TableCell>
                  {value ?? ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

//------------------------------------------------
// Suppliers
//------------------------------------------------
const Suppliers = (props) => {
  const { t } = useTranslation();

  const {
    _suppliersList,
    _getById,
    _delete,
    _store,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [selectedSupplier, setSelectedSupplier] =
    useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Suppliers by Store
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Supplier + controller.GetByStore,
        _store.id,
        actions.ActionType.SetSuppliers,
        () => setLoader(false)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Supplier Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (!Array.isArray(_suppliersList)) {
      return [];
    }

    return _suppliersList.map((obj) => ({
      ...obj,

      name: [
        obj.firstName,
        obj.lastName,
      ]
        .filter(Boolean)
        .join(" "),
    }));
  }, [_suppliersList]);

  //------------------------------------------------
  // Add / Edit Supplier
  //------------------------------------------------
  const handleAddEdit = (supplier = null) => {
    navigate(
      supplier?.id
        ? `supplier-form/${supplier.id}`
        : "supplier-form"
    );
  };

  //------------------------------------------------
  // View Supplier Details
  //------------------------------------------------
  const handleView = (data) => {
    const original = Array.isArray(_suppliersList)
      ? _suppliersList.find(
          (supplier) =>
            String(supplier.id) === String(data.id)
        )
      : null;

    setSelectedSupplier(original || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setSelectedSupplier(null);
  };

  //------------------------------------------------
  // Delete Supplier
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("dangerousAction"),
      subTitle: t("deleteSupplierWarning"),

      onConfirm: () => {
        setLoader(true);

        const onComplete = (res) => {
          setLoader(false);

          setAlert({
            isOpen: true,
            type:
              res?.status === 200
                ? "success"
                : "error",
            title:
              res?.status === 200
                ? t("deletedSuccessfully")
                : t("error"),
            subTitle:
              res?.msg || t("requestFailed"),
          });
        };

        _delete(
          endpoints.Supplier,
          id,
          actions.ActionType.RmvSupplier,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "company", label: t("companyName") },
    { id: "name", label: t("contactPerson") },
    { id: "phone", label: t("phone") },
    { id: "address", label: t("address") },
    { id: "productSale", label: t("productsSale") },
    { id: "note", label: t("note") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisRecord")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisRecord")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Supplier Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addSupplier")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("suppliers")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("suppliers")}
        />
      </Box>

      {/* Normal Suppliers Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Suppliers List */}
      <Box
        ref={reportRef}
        className="suppliers-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("suppliers")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View Supplier Details Dialog */}
      <Dialog
        show={showView}
        maxWidth="sm"
        onClose={handleCloseView}
      >
        {selectedSupplier && (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("supplierDetails")}
              />
            </Box>

            <SupplierDetailsContent
              supplier={selectedSupplier}
              t={t}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Button
                onClick={handleCloseView}
                className="btn-error"
                size="large"
                sx={{ minWidth: "150px" }}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Printable Supplier Details */}
      {selectedSupplier && (
        <Box
          ref={detailsRef}
          className="supplier-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <SupplierDetailsContent
            supplier={selectedSupplier}
            t={t}
          />
        </Box>
      )}

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  _suppliersList: state.pos.suppliersList,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Suppliers);
